import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { NotificationPreferences } from '@/components/settings/notification-preferences';
import { DeepWorkConfig } from '@/components/settings/deep-work-config';
import { useUserProfile, useUpdateUserProfile } from '@/hooks/use-user-profile';
import {
  useWorkspaces,
  useUpdateWorkspace,
  useDeleteWorkspace,
} from '@/hooks/use-workspaces';
import { useThemeStore } from '@/stores/theme-store';
import { Sun, Moon, Monitor, ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/_authenticated/settings')({
  component: SettingsPage,
});

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const COMMON_TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Sao_Paulo',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Moscow',
  'Asia/Dubai',
  'Asia/Kolkata',
  'Asia/Shanghai',
  'Asia/Tokyo',
  'Asia/Singapore',
  'Australia/Sydney',
  'Pacific/Auckland',
];

/* ------------------------------------------------------------------ */
/*  Profile Tab                                                        */
/* ------------------------------------------------------------------ */

function ProfileTab() {
  const { data: profile, isLoading } = useUserProfile();
  const updateProfile = useUpdateUserProfile();

  const [displayName, setDisplayName] = useState('');
  const [timezone, setTimezone] = useState('');
  const [availableHours, setAvailableHours] = useState('');
  const [initialized, setInitialized] = useState(false);

  // Sync initial values from server
  if (profile && !initialized) {
    setDisplayName(profile.displayName);
    setTimezone(profile.timezone);
    setAvailableHours(String(profile.availableHoursPerDay));
    setInitialized(true);
  }

  if (isLoading || !profile) {
    return (
      <div className="space-y-4">
        {[1, 2].map((i) => (
          <div key={i} className="h-32 animate-pulse rounded-xl bg-muted" />
        ))}
      </div>
    );
  }

  const handleNameBlur = () => {
    if (displayName.trim() && displayName.trim() !== profile.displayName) {
      updateProfile.mutate({ displayName: displayName.trim() });
    }
  };

  const handleTimezoneChange = (value: string) => {
    setTimezone(value);
    updateProfile.mutate({ timezone: value });
  };

  const handleHoursBlur = () => {
    const parsed = parseFloat(availableHours);
    if (!isNaN(parsed) && parsed >= 0.5 && parsed <= 24 && parsed !== profile.availableHoursPerDay) {
      updateProfile.mutate({ availableHoursPerDay: parsed });
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Profile</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="display-name">Display Name</Label>
            <Input
              id="display-name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              onBlur={handleNameBlur}
              placeholder="Your display name"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="timezone">Timezone</Label>
            <Select value={timezone} onValueChange={handleTimezoneChange}>
              <SelectTrigger>
                <SelectValue placeholder="Select timezone" />
              </SelectTrigger>
              <SelectContent>
                {COMMON_TIMEZONES.map((tz) => (
                  <SelectItem key={tz} value={tz}>
                    {tz.replace(/_/g, ' ')}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="available-hours">Available Hours per Day</Label>
            <Input
              id="available-hours"
              type="number"
              min="0.5"
              max="24"
              step="0.5"
              value={availableHours}
              onChange={(e) => setAvailableHours(e.target.value)}
              onBlur={handleHoursBlur}
              className="w-[120px]"
            />
            <p className="text-xs text-muted-foreground">
              Used for time-budget calculations in My Day
            </p>
          </div>
        </CardContent>
      </Card>

      <DeepWorkConfig />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Workspaces Tab                                                      */
/* ------------------------------------------------------------------ */

function WorkspaceCard({ workspace }: { workspace: { id: string; name: string; type: string; activeBatchLimit: number; role: string } }) {
  const [expanded, setExpanded] = useState(false);
  const [name, setName] = useState(workspace.name);
  const updateWorkspace = useUpdateWorkspace(workspace.id);
  const deleteWorkspace = useDeleteWorkspace(workspace.id);

  const isOwner = workspace.role === 'owner';
  const isAdminOrOwner = workspace.role === 'owner' || workspace.role === 'admin';

  const handleNameBlur = () => {
    if (name.trim() && name.trim() !== workspace.name) {
      updateWorkspace.mutate({ name: name.trim() });
    }
  };

  const handleLimitChange = (value: string) => {
    updateWorkspace.mutate({ active_batch_limit: parseInt(value) });
  };

  return (
    <Card>
      <button
        type="button"
        className="flex w-full items-center justify-between px-6 py-4"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <span className="text-sm font-semibold">{workspace.name}</span>
          <Badge variant="outline" className="capitalize text-xs">
            {workspace.type}
          </Badge>
        </div>
        {expanded ? (
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        )}
      </button>

      {expanded && (
        <CardContent className="space-y-4 border-t pt-4">
          <div className="space-y-2">
            <Label htmlFor={`ws-name-${workspace.id}`}>Workspace Name</Label>
            <Input
              id={`ws-name-${workspace.id}`}
              value={name}
              onChange={(e) => setName(e.target.value)}
              onBlur={handleNameBlur}
              disabled={!isAdminOrOwner}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor={`ws-limit-${workspace.id}`}>Focus Limit</Label>
            <Select
              value={String(workspace.activeBatchLimit)}
              onValueChange={handleLimitChange}
              disabled={!isAdminOrOwner}
            >
              <SelectTrigger className="w-[100px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[1, 2, 3, 4, 5, 6, 7].map((n) => (
                  <SelectItem key={n} value={String(n)}>
                    {n}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Maximum recommended active projects at once
            </p>
          </div>

          {isOwner && (
            <div className="pt-4 border-t">
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" size="sm">
                    Delete Workspace
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete workspace?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will permanently delete &quot;{workspace.name}&quot; and all its
                      projects and tasks. This action cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => deleteWorkspace.mutate()}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}

function WorkspacesTab() {
  const { data: workspaces, isLoading } = useWorkspaces();

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2].map((i) => (
          <div key={i} className="h-20 animate-pulse rounded-xl bg-muted" />
        ))}
      </div>
    );
  }

  if (!workspaces || workspaces.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-8 text-center">
        No workspaces found.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      {workspaces.map((ws) => (
        <WorkspaceCard key={ws.id} workspace={ws} />
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Appearance Tab                                                      */
/* ------------------------------------------------------------------ */

function AppearanceTab() {
  const { theme, setTheme } = useThemeStore();

  const options: { value: 'system' | 'light' | 'dark'; label: string; icon: typeof Sun }[] = [
    { value: 'light', label: 'Light', icon: Sun },
    { value: 'dark', label: 'Dark', icon: Moon },
    { value: 'system', label: 'System', icon: Monitor },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Theme</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex gap-3">
          {options.map(({ value, label, icon: Icon }) => (
            <button
              key={value}
              type="button"
              onClick={() => setTheme(value)}
              className={cn(
                'flex flex-col items-center gap-2 rounded-lg border px-6 py-4 text-sm font-medium transition-colors',
                theme === value
                  ? 'border-primary bg-primary/5 text-foreground'
                  : 'border-border text-muted-foreground hover:bg-muted'
              )}
            >
              <Icon className="h-5 w-5" />
              {label}
            </button>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/*  Main Settings Page                                                  */
/* ------------------------------------------------------------------ */

function SettingsPage() {
  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Settings</h1>
        <p className="text-muted-foreground">
          Manage your profile, workspaces, and preferences.
        </p>
      </div>

      <Tabs defaultValue="profile">
        <TabsList className="w-full justify-start">
          <TabsTrigger value="profile">Profile</TabsTrigger>
          <TabsTrigger value="workspaces">Workspaces</TabsTrigger>
          <TabsTrigger value="notifications">Notifications</TabsTrigger>
          <TabsTrigger value="appearance">Appearance</TabsTrigger>
        </TabsList>

        <TabsContent value="profile">
          <ProfileTab />
        </TabsContent>

        <TabsContent value="workspaces">
          <WorkspacesTab />
        </TabsContent>

        <TabsContent value="notifications">
          <NotificationPreferences />
        </TabsContent>

        <TabsContent value="appearance">
          <AppearanceTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
